import type { FC, MouseEvent, ChangeEvent, SyntheticEvent } from 'react';
import React, { useState, createRef, useRef, useMemo, useEffect } from 'react';
import type { THandler, TKeyboardEventHandler } from '@alfa-bank/site-pure-input/src/models';

import { getPlaceholderMask } from '../helpers';

import {
    getInitialCursor,
    getCursorFromRef,
    getCursorFromValue,
    closestEditableIndex,
    generatePlaceholder,
} from './helpers';
import { useCursorPosition } from './use-cursor-position';
import type { TPhoneInput, TInputSelection, TFocusHandler, TOnChange } from './models';
import { DATA_TEST_ID, defaultHandler, DEFAULT_MASK, NUMBER_SYMBOL } from './config';
import { usePrefixSelection } from './use-prefix-selection';
import { useValueFromQuery } from './use-value-from-query';
import { useMaskFormattingTools } from './use-mask-formatting-tools';

export const PhoneInput: FC<TPhoneInput> = ({
    Input,
    dataTestId = DATA_TEST_ID,
    forwardedRef,
    mask = DEFAULT_MASK,
    onCancel = defaultHandler,
    onChange = defaultHandler,
    onKeyDown = defaultHandler,
    placeholderNumberSymbol,
    styles,
    queryName,
    value: initValue,
    hasPlaceholderMask,
    updateIsEmpty,
    ...rest
}) => {
    const { formatValue, maskOptions, removeMaskNumbersAfterDeletedSymbol } =
        useMaskFormattingTools(mask);

    const [value, setValue] = useState(() => formatValue(String(initValue || maskOptions.prefix)));
    const initialCursor = getInitialCursor(maskOptions.prefix.length);

    const inputRef = forwardedRef || createRef();
    const isDeleteOperation = useRef<boolean>(false);
    const hasFocus = useRef<boolean>(false);
    const cursorPosition = useRef<TInputSelection>(initialCursor);

    const position = cursorPosition.current.start;
    const isDeleting = isDeleteOperation.current;
    useCursorPosition({ hasFocus, position, inputRef, maskOptions, isDeleting });
    usePrefixSelection(inputRef, maskOptions.prefix);

    /** Генерация полного плейсхолдера на основе маски */
    const generatedPlaceholder = useMemo(
        () =>
            generatePlaceholder(
                maskOptions.maskFull.asString,
                NUMBER_SYMBOL,
                placeholderNumberSymbol
            ),
        [maskOptions.maskFull.asString, placeholderNumberSymbol]
    );

    /** Генерация отображаемого плейсхолдера на основе введенного значения и полного плейсхолдера */
    const placeholderMask = useMemo(
        () => getPlaceholderMask(value, generatedPlaceholder),
        [value, generatedPlaceholder]
    );

    const handleChange: TOnChange = eventProps => {
        const { event, name, value: eventValue } = eventProps;
        let newValue = String(eventValue);
        // Safari вставляет номер телефона без регионального кода, т.е. без +7
        if (!newValue.startsWith(maskOptions.prefix)) {
            newValue = maskOptions.prefix.concat(newValue);
        }
        const isNotInitialValue = value !== maskOptions.prefix;
        const newValueIsLongerThanField =
            newValue.toString().length > maskOptions.maskFull.asString.length;

        /**
         * Предотвратить набор, если поле заполнено.
         * Проверка на изначальное значение требуется, чтобы оставить возможность
         * вставить телефон из буфера обмена (в таком случае новое значение до форматирования в любом случае будет длинее поля)
         */
        if (isNotInitialValue && newValueIsLongerThanField) return;

        const preformattedValue = removeMaskNumbersAfterDeletedSymbol(newValue, value);
        const formattedValue = formatValue(preformattedValue);

        setValue(formattedValue);

        cursorPosition.current =
            initValue === '' || initValue === maskOptions.prefix
                ? getCursorFromValue(formattedValue)
                : getCursorFromRef(inputRef);
        onChange({ value: formattedValue, name, event });
        event?.preventDefault?.();
    };

    const handleDeleteSymbol = (event: SyntheticEvent<HTMLInputElement>) => {
        const { current: input } = inputRef;
        isDeleteOperation.current = true;

        const hasSelection = input?.selectionStart !== input?.selectionEnd;

        // если выделенаа конкретная часть текста которую требуется удалить, то достаточно стандартного поведения
        if (hasSelection) {
            return;
        }

        event.preventDefault();

        // индекс ближайшего числа, которое можно изменить
        const closestIndex = closestEditableIndex({ input, maskOptions });

        const finalValue = closestIndex
            ? // если такое число существует, то сохраняется строка до и после него, а само число удаляется
              value.slice(0, closestIndex) + value.slice(closestIndex + 1)
            : // если нет, то значение остается прежним
              value;

        // курсор перемеещается на место удаленного числа
        if (closestIndex) {
            input?.setSelectionRange(closestIndex, closestIndex);
        }

        // событие передается дальше в хэндлер handleChange, вместе с новым значением
        const e = event as ChangeEvent<HTMLInputElement>;
        handleChange({ value: finalValue, event: e, name: '' });
    };

    const handleKeyDown: TKeyboardEventHandler = eventProps => {
        const { prefix } = maskOptions;
        const { event, name, value: newValue } = eventProps;
        const { key, target } = event;
        const { selectionStart } = target as HTMLInputElement;
        const isCursorOnPrefix = Number(selectionStart) <= prefix.length;
        cursorPosition.current = getCursorFromRef(inputRef);

        // не пускать каретку левее скобочек "("
        if (key === 'ArrowLeft' && isCursorOnPrefix) {
            event.preventDefault();
        }

        if (key === 'Backspace') {
            handleDeleteSymbol(event);
        } else {
            isDeleteOperation.current = false;
        }

        onKeyDown({ event, name, value: String(newValue) });
    };

    const handleCancel = (event: MouseEvent<HTMLButtonElement>) => {
        const formattedValue = formatValue('');
        setValue(formattedValue);

        onCancel(event);
    };

    const handleFocus: TFocusHandler = event => {
        const { onFocus } = rest;

        hasFocus.current = true;

        onFocus?.(event);
    };

    const handleBlur: THandler<React.FocusEvent<HTMLInputElement>> | undefined = event => {
        const { onBlur } = rest;

        hasFocus.current = false;
        onBlur?.(event);
    };

    useEffect(() => {
        /**
         * TODO: флаг updateIsEmpty нужен для того, чтобы сбрасывать значение в случае, если поле пустое,
         * задача на доработку https://jira.moscow.alfaintra.net/browse/ALFABANKRU-74941
         */
        const isEmpty = updateIsEmpty && initValue === '';
        const isNotEmpty = String(initValue).length >= maskOptions.prefix.length;

        const isNotInitialValue = initValue ? isNotEmpty : isEmpty;

        if (initValue === value || !isNotInitialValue) return;

        handleChange({ name: rest.name, value: String(initValue) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initValue]);

    useValueFromQuery(queryName, rest.name, handleChange);

    return (
        <div className={styles.wrapper}>
            {hasPlaceholderMask && (
                <div className={styles.placeholderWrapper}>
                    <span className={styles.placeholderHidden}>{value}</span>
                    {placeholderMask}
                </div>
            )}

            <Input
                dataTestId={dataTestId}
                forwardedRef={inputRef}
                type="tel"
                {...rest}
                onBlur={handleBlur}
                onCancel={handleCancel}
                onChange={handleChange}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                value={value}
            />
        </div>
    );
};
